import axios from "axios";
import { sendSentryEvent } from "./sentryUtils";
import { refreshToken } from "./refreshTokenHelper";

// Global state for token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Create axios instance with token refresh interceptors
export const createAxiosInstanceWithTokenRefresh = (
  baseURL: string,
  errorType = "API error"
) => {
  const instance = axios.create({
    baseURL,
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = token;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        return new Promise((resolve, reject) => {
          refreshToken()
            .then((newToken) => {
              originalRequest.headers["Authorization"] = newToken;
              processQueue(null, newToken);
              resolve(instance(originalRequest));
            })
            .catch((err) => {
              processQueue(err, null);
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      sendSentryEvent(error, errorType);
      return Promise.reject(error);
    }
  );

  instance.interceptors.request.use((config) => {
    const token = window.localStorage.getItem("JWT");
    config.headers.Authorization = token;
    return config;
  });

  return instance;
};

// Create cloud core API instance
export const createCloudCoreInstance = () => {
  const CLOUD_CORE_API_URL = window._envK8S_?.CLOUD_CORE_API_URL;
  if (!CLOUD_CORE_API_URL) return null;

  return createAxiosInstanceWithTokenRefresh(
    CLOUD_CORE_API_URL,
    "Cloud Core API error"
  );
};

// Create cloud users API instance
export const createCloudUsersInstance = () => {
  const API_URL = window._envK8S_?.REACT_APP_API_URL;
  if (!API_URL) return null;

  const cloudUsersURL = API_URL.replace("com", "net").replace(
    "map-editor",
    "cloud"
  );
  return createAxiosInstanceWithTokenRefresh(
    cloudUsersURL,
    "Cloud Users API error"
  );
};
